import { useState, useEffect, useCallback, useMemo, useRef } from 'react';
import { useDentalRecords } from '../../hooks/useDentalRecords';
import { usePatientTabs } from '../../hooks/usePatientTabs';
import { useSeance } from '../../hooks/useSeance';
import DentalChartPro from './DentalChartPro';
import type { ChartSelectionData } from './DentalChartPro';
import SmartNGAPPanel from './SmartNGAPPanel';
import DentalStatesPanel from './DentalStatesPanel';
import PatientDocumentsPanel from './PatientDocumentsPanel';
import SeanceHistoryTable from './SeanceHistoryTable';
import PatientTabBar from './PatientTabBar';
import ConfirmDialog from './ConfirmDialog';
import type { ConfirmDialogVariant } from './ConfirmDialog';
import type { NGAPAct } from '../../types/records';
import { patientService, type Patient } from '../../services/patientService';

interface ConfirmState {
  open: boolean;
  title: string;
  message: string;
  details?: string;
  variant: ConfirmDialogVariant;
  confirmLabel: string;
  onConfirm: () => void;
}

type LeftPanelMode = 'ngap' | 'states' | 'documents';
type JawView = 'full' | 'upper' | 'lower';

// ── Insurance badge colors (theme-aware: use opacity-based colors that work in both themes) ──
const INSURANCE_COLORS: Record<string, { bg: string; text: string; border: string }> = {
  CNOPS: { bg: 'bg-blue-500/10', text: 'text-blue-600 dark:text-blue-400', border: 'border-blue-500/20' },
  CNSS: { bg: 'bg-emerald-500/10', text: 'text-emerald-600 dark:text-emerald-400', border: 'border-emerald-500/20' },
  FAR: { bg: 'bg-orange-500/10', text: 'text-orange-600 dark:text-orange-400', border: 'border-orange-500/20' },
  CMIM: { bg: 'bg-purple-500/10', text: 'text-purple-600 dark:text-purple-400', border: 'border-purple-500/20' },
  other: { bg: 'bg-muted', text: 'text-muted-foreground', border: 'border-border' },
  none: { bg: 'bg-muted', text: 'text-muted-foreground', border: 'border-border/50' },
};

function calcAge(birthDate: string | undefined): number | null {
  if (!birthDate) return null;
  const d = new Date(birthDate);
  if (isNaN(d.getTime())) return null;
  const now = new Date();
  let age = now.getFullYear() - d.getFullYear();
  const m = now.getMonth() - d.getMonth();
  if (m < 0 || (m === 0 && now.getDate() < d.getDate())) age--;
  return age;
}

export default function RecordsClientV2() {
  // ── Patient list ──
  const [patients, setPatients] = useState<Patient[]>([]);
  const [patientSearch, setPatientSearch] = useState('');
  const [patientsLoading, setPatientsLoading] = useState(false);
  const [showPatientDropdown, setShowPatientDropdown] = useState(false);
  const dropdownRef = useRef<HTMLDivElement>(null);

  // ── Confirm dialog state ──
  const [confirmState, setConfirmState] = useState<ConfirmState>({
    open: false, title: '', message: '', variant: 'warning', confirmLabel: 'Confirmer', onConfirm: () => {},
  });
  const closeConfirm = useCallback(() => setConfirmState(s => ({ ...s, open: false })), []);

  // ── Tab system ──
  const { tabs, activeTabId, openTab, closeTab, switchTab } = usePatientTabs();

  // ── Close dropdown on outside click ──
  useEffect(() => {
    if (!showPatientDropdown) return;
    const handler = (e: MouseEvent) => {
      if (dropdownRef.current && !dropdownRef.current.contains(e.target as Node)) {
        setShowPatientDropdown(false);
      }
    };
    document.addEventListener('mousedown', handler);
    return () => document.removeEventListener('mousedown', handler);
  }, [showPatientDropdown]);

  // ── Load patients ──
  useEffect(() => {
    let cancelled = false;
    setPatientsLoading(true);
    patientService
      .getPatients()
      .then((p) => {
        if (!cancelled) setPatients(p);
      })
      .catch((err) => console.error('Error loading patients:', err))
      .finally(() => {
        if (!cancelled) setPatientsLoading(false);
      });
    return () => { cancelled = true; };
  }, []);

  const filteredPatients = useMemo(() => {
    if (!patientSearch.trim()) return patients;
    const q = patientSearch.toLowerCase();
    return patients.filter(
      (p) =>
        p.firstName?.toLowerCase().includes(q) ||
        p.lastName?.toLowerCase().includes(q) ||
        `${p.firstName} ${p.lastName}`.toLowerCase().includes(q)
    );
  }, [patients, patientSearch]);

  // ── Patient lookup Map (O(1) instead of O(n) find) ──
  const patientMap = useMemo(() => {
    const m = new Map<string, Patient>();
    for (const p of patients) m.set(p.id, p);
    return m;
  }, [patients]);

  const selectedPatient = useMemo(
    () => (activeTabId ? patientMap.get(activeTabId) ?? null : null),
    [patientMap, activeTabId]
  );

  // ── Dental records hook (keyed on active tab) ──
  const {
    teethData,
    treatments,
    categories,
    chartLoading,
    selectedTooth,
    selectedAct,
    selectionContext,
    sessionExtractions,
    selectTooth,
    selectAct,
    clearSelection,
    setChartSelection,
    applyAct,
    applyActLoading,
    quickApplyState,
    removeToothLayer,
    completeTreatment: _completeTreatment,
    deleteTreatment,
    error,
    clearError,
    validationMessage,
    validationGuidance,
    validationWarnings,
    clearValidation,
  } = useDentalRecords({ patientId: activeTabId });

  // ── Séance hook (keyed on active tab) ──
  const {
    activeSeance: _activeSeance,
    allSeances,
    isSeanceOpen,
    seanceAmounts,
    grandTotals,
    seanceTreatments,
    startSeance,
    closeSeance,
    refreshSeances,
    error: seanceError,
    clearError: clearSeanceError,
  } = useSeance(activeTabId);

  // ── Panel visibility & view mode ──
  const [leftPanelMode, setLeftPanelMode] = useState<LeftPanelMode>('states');
  const [showLeftPanel, setShowLeftPanel] = useState(true);
  const [showHistoryPanel, setShowHistoryPanel] = useState(true);
  const [jawView, setJawView] = useState<JawView>('full');
  const lastJawScrollRef = useRef(0);

  // ── Patient selection: opens a tab ──
  const handleSelectPatient = useCallback((patient: Patient) => {
    openTab(patient);
    setPatientSearch('');
    setShowPatientDropdown(false);
    clearSelection();
  }, [openTab, clearSelection]);

  // ── Handle act selection from Smart NGAP panel ──
  const handleActSelect = useCallback(
    (act: NGAPAct) => {
      selectAct(act);
    },
    [selectAct]
  );

  // ── Handle dental state application ──
  const handleApplyState = useCallback(
    async (stateCode: string) => {
      // Auto-start séance if none open
      if (!isSeanceOpen && activeTabId) {
        await startSeance();
      }
      await quickApplyState(stateCode);
      // Refresh séance to pick up new treatments
      await refreshSeances();
    },
    [quickApplyState, isSeanceOpen, activeTabId, startSeance, refreshSeances]
  );

  // ── Handle NGAP act apply (overridden to auto-start séance) ──
  const handleApplyAct = useCallback(async () => {
    if (!isSeanceOpen && activeTabId) {
      await startSeance();
    }
    await applyAct();
    await refreshSeances();
  }, [applyAct, isSeanceOpen, activeTabId, startSeance, refreshSeances]);

  // ── Handle layer removal ──
  const handleRemoveLayer = useCallback(
    (toothNum: number, layerIndex: number) => {
      removeToothLayer(toothNum, layerIndex);
    },
    [removeToothLayer]
  );

  // ── Handle chart selection changes ──
  const handleSelectionChange = useCallback(
    (sel: ChartSelectionData) => {
      setChartSelection(sel);
    },
    [setChartSelection]
  );

  // ── Handle close séance (with confirmation) ──
  const handleCloseSeance = useCallback(() => {
    const count = seanceTreatments.length;
    const total = seanceAmounts.totalDue;
    setConfirmState({
      open: true,
      title: 'Terminer cette séance ?',
      message: 'Cette action clôturera la séance en cours. Elle ne pourra plus être modifiée.',
      details: `${count} acte${count !== 1 ? 's' : ''} — ${total.toFixed(0)} DHS`,
      variant: 'warning',
      confirmLabel: 'Terminer la séance',
      onConfirm: () => {
        closeConfirm();
        closeSeance();
      },
    });
  }, [closeSeance, closeConfirm, seanceTreatments.length, seanceAmounts.totalDue]);

  // ── Handle treatment deletion (with confirmation, refresh séance too) ──
  const handleDeleteTreatment = useCallback((id: string) => {
    setConfirmState({
      open: true,
      title: 'Supprimer cet acte ?',
      message: 'L\'acte sera retiré de la séance en cours. Cette action est irréversible.',
      variant: 'danger',
      confirmLabel: 'Supprimer',
      onConfirm: async () => {
        closeConfirm();
        await deleteTreatment(id);
        await refreshSeances();
      },
    });
  }, [deleteTreatment, refreshSeances, closeConfirm]);

  // ── Jaw scroll auto-switch with debounce ──
  const handleJawScroll = useCallback((e: React.WheelEvent) => {
    const now = Date.now();
    if (now - lastJawScrollRef.current < 500) return;
    if (Math.abs(e.deltaY) < 15) return;
    lastJawScrollRef.current = now;
    if (e.deltaY > 0) {
      setJawView(prev => prev === 'upper' ? 'full' : prev === 'full' ? 'lower' : prev);
    } else {
      setJawView(prev => prev === 'lower' ? 'full' : prev === 'full' ? 'upper' : prev);
    }
  }, []);

  // Determine if Apply button should be enabled
  const allSelectedTeeth = selectionContext?.selectedTeeth?.length ? selectionContext.selectedTeeth : (selectedTooth ? [selectedTooth] : []);
  const canApply = allSelectedTeeth.length > 0 && selectedAct !== null && !applyActLoading;

  // ── Patient info tags ──
  const patientAge = calcAge(selectedPatient?.birthDate || selectedPatient?.dateOfBirth);
  const insuranceKey = selectedPatient?.insurance || 'none';
  const insuranceStyle = INSURANCE_COLORS[insuranceKey] || INSURANCE_COLORS.none;
  const allergies = useMemo(() => {
    if (selectedPatient?.medical_history_allergies) {
      return selectedPatient.medical_history_allergies.split(',').map(a => a.trim()).filter(Boolean);
    }
    return selectedPatient?.medicalConditions || [];
  }, [selectedPatient?.medical_history_allergies, selectedPatient?.medicalConditions]);

  // ── Memoized treatment counts (avoid .filter() in render) ──
  const treatmentCounts = useMemo(() => {
    let completed = 0;
    let planned = 0;
    for (const t of treatments) {
      if (t.status === 'COMPLETED') completed++;
      else if (t.status === 'PLANNED') planned++;
    }
    return { completed, planned };
  }, [treatments]);

  // Combined error
  const displayError = error || seanceError;

  // ── Dynamic panel widths: redistribute space when panels are toggled ──
  const leftPanelWidth = useMemo(() => {
    if (!showLeftPanel) return '';
    // Solo left panel → wider to use freed chart dead-space
    if (!showHistoryPanel) return 'w-80';
    return 'w-72';
  }, [showLeftPanel, showHistoryPanel]);

  const rightPanelWidth = useMemo(() => {
    if (!showHistoryPanel) return '';
    // Solo right panel → wider
    if (!showLeftPanel) return 'w-96';
    return 'w-80';
  }, [showHistoryPanel, showLeftPanel]);

  return (
    <div className="h-[calc(100vh-80px)] flex flex-col bg-background min-w-[900px]">
      {/* ── Top Bar ── */}
      <div className="shrink-0 flex items-center gap-3 px-5 py-2 bg-card/95 backdrop-blur-sm border-b border-border" style={{ zIndex: showPatientDropdown ? 50 : 'auto', position: 'relative' }}>
        {/* Patient search */}
        <div className="relative flex-shrink-0 w-72" ref={dropdownRef} style={{ zIndex: showPatientDropdown ? 9999 : 'auto' }}>
          <div className="relative">
            <svg
              className="absolute left-3 top-1/2 -translate-y-1/2 h-4 w-4 text-muted-foreground"
              fill="none" stroke="currentColor" viewBox="0 0 24 24"
            >
              <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2}
                d="M16 7a4 4 0 11-8 0 4 4 0 018 0zM12 14a7 7 0 00-7 7h14a7 7 0 00-7-7z" />
            </svg>
            <input
              type="text"
              value={patientSearch}
              onChange={(e) => {
                setPatientSearch(e.target.value);
                setShowPatientDropdown(true);
              }}
              onFocus={() => setShowPatientDropdown(true)}
              placeholder="Rechercher un patient..."
              className="w-full pl-9 pr-3 py-1.5 text-[12px] bg-muted border border-border rounded-lg 
                focus:outline-none focus:ring-2 focus:ring-primary/30 focus:border-primary/40 focus:bg-muted/80
                text-foreground placeholder-muted-foreground transition-all duration-200"
            />
          </div>

          {showPatientDropdown && (
            <div
              className="absolute left-0 right-0 mt-1.5 bg-popover border border-border rounded-xl shadow-2xl shadow-black/20 dark:shadow-black/60 max-h-72 overflow-y-auto ring-1 ring-black/[0.05] dark:ring-white/[0.05] scrollbar-thin"
              style={{ zIndex: 9999 }}
            >
              {patientsLoading ? (
                <div className="flex items-center gap-2.5 px-4 py-4">
                  <svg className="h-4 w-4 animate-spin text-primary" viewBox="0 0 24 24">
                    <circle className="opacity-25" cx="12" cy="12" r="10" stroke="currentColor" strokeWidth="4" fill="none" />
                    <path className="opacity-75" fill="currentColor" d="M4 12a8 8 0 018-8V0C5.373 0 0 5.373 0 12h4z" />
                  </svg>
                  <span className="text-[11px] text-muted-foreground">Chargement des patients...</span>
                </div>
              ) : filteredPatients.length === 0 ? (
                <div className="flex flex-col items-center gap-1.5 px-4 py-5">
                  <svg className="h-5 w-5 text-muted-foreground/60" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                    <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={1.5} d="M21 21l-6-6m2-5a7 7 0 11-14 0 7 7 0 0114 0z" />
                  </svg>
                  <span className="text-[11px] text-muted-foreground">Aucun patient trouvé</span>
                </div>
              ) : (
                <div className="py-1">
                  <div className="px-3 py-1.5">
                    <span className="text-[9px] font-semibold uppercase tracking-widest text-muted-foreground">
                      {filteredPatients.length} patient{filteredPatients.length !== 1 ? 's' : ''}
                    </span>
                  </div>
                  {filteredPatients.slice(0, 20).map((p, idx) => (
                    <button
                      key={p.id}
                      onClick={() => handleSelectPatient(p)}
                      className={`w-full text-left px-3 py-2 flex items-center gap-2.5 transition-all duration-150
                        hover:bg-primary/10 focus:bg-primary/10 focus:outline-none
                        ${idx > 0 ? 'border-t border-border/30' : ''}`}
                    >
                      <div className="w-8 h-8 rounded-full bg-gradient-to-br from-primary to-primary/80 flex items-center justify-center text-[11px] font-bold text-white shrink-0 shadow-sm shadow-primary/25 border border-primary/20">
                        {p.firstName?.charAt(0)?.toUpperCase()}{p.lastName?.charAt(0)?.toUpperCase()}
                      </div>
                      <div className="min-w-0 flex-1">
                        <div className="text-[12px] text-foreground font-medium truncate leading-tight">
                          {p.firstName} {p.lastName}
                        </div>
                        <div className="flex items-center gap-1.5 mt-0.5">
                          {(p.phone || p.email) && (
                            <span className="text-[10px] text-muted-foreground truncate">
                              {p.phone || p.email}
                            </span>
                          )}
                          {p.insurance && p.insurance !== 'none' && (
                            <span className={`text-[9px] font-semibold px-1.5 py-px rounded-full border ${
                              (INSURANCE_COLORS[p.insurance] || INSURANCE_COLORS.other).bg
                            } ${(INSURANCE_COLORS[p.insurance] || INSURANCE_COLORS.other).text} ${
                              (INSURANCE_COLORS[p.insurance] || INSURANCE_COLORS.other).border
                            }`}>
                              {p.insurance}
                            </span>
                          )}
                        </div>
                      </div>
                      <svg className="w-3.5 h-3.5 text-muted-foreground/60 shrink-0" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                        <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M9 5l7 7-7 7" />
                      </svg>
                    </button>
                  ))}
                  {filteredPatients.length > 20 && (
                    <div className="px-3 py-2 text-center border-t border-border/30">
                      <span className="text-[10px] text-muted-foreground">
                        +{filteredPatients.length - 20} patients non affichés
                      </span>
                    </div>
                  )}
                </div>
              )}
            </div>
          )}
        </div>

        {/* Selected act / apply badge */}
        {activeTabId && (
          <div className="ml-auto flex items-center gap-2">
            {selectedAct && (
              <div className="flex items-center gap-2 px-3 py-1 bg-primary/10 border border-primary/20 rounded-lg">
                <span className="font-mono text-[10px] text-primary font-semibold">{selectedAct.code}</span>
                <span className="text-[10px] text-muted-foreground max-w-[180px] truncate">{selectedAct.label}</span>
                {allSelectedTeeth.length > 0 && (
                  <>
                    <span className="text-muted-foreground/60">→</span>
                    <span className="text-[10px] font-mono text-primary">
                      {allSelectedTeeth.length === 1
                        ? `#${allSelectedTeeth[0]}`
                        : `${allSelectedTeeth.length} dents`
                      }
                    </span>
                  </>
                )}
              </div>
            )}
            <button
              onClick={handleApplyAct}
              disabled={!canApply}
              className={`px-4 py-1.5 text-xs font-semibold rounded-lg transition-all focus:outline-none focus:ring-2 focus:ring-primary/50 ${
                canApply
                  ? 'bg-primary hover:bg-primary/90 text-primary-foreground shadow-lg shadow-primary/30 active:scale-95'
                  : 'bg-muted text-muted-foreground cursor-not-allowed'
              }`}
            >
              {applyActLoading ? (
                <span className="flex items-center gap-1.5">
                  <svg className="h-3 w-3 animate-spin" viewBox="0 0 24 24">
                    <circle className="opacity-25" cx="12" cy="12" r="10" stroke="currentColor" strokeWidth="4" fill="none" />
                    <path className="opacity-75" fill="currentColor" d="M4 12a8 8 0 018-8V0C5.373 0 0 5.373 0 12h4z" />
                  </svg>
                  Application...
                </span>
              ) : (
                'Appliquer'
              )}
            </button>
            <button
              onClick={clearSelection}
              className="p-1.5 rounded-md hover:bg-muted text-muted-foreground transition-colors"
              title="Effacer la sélection"
            >
              <svg className="h-4 w-4" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M6 18L18 6M6 6l12 12" />
              </svg>
            </button>
          </div>
        )}
      </div>

      {/* ── Patient Tab Bar ── */}
      <PatientTabBar
        tabs={tabs}
        activeTabId={activeTabId}
        onSwitchTab={switchTab}
        onCloseTab={closeTab}
        onAddTab={() => {
          setPatientSearch('');
          setShowPatientDropdown(true);
          // Focus search input
          setTimeout(() => {
            const input = dropdownRef.current?.querySelector('input');
            input?.focus();
          }, 50);
        }}
      />

      {/* ── Patient Info Bar (insurance, allergies, age, sex) ── */}
      {activeTabId && selectedPatient && (
        <div className="shrink-0 flex items-center gap-2 px-4 py-1.5 bg-card/60 border-b border-border/30">
          {/* Name + avatar */}
          <div className="flex items-center gap-2 mr-2">
            <div className="w-6 h-6 rounded-full bg-gradient-to-br from-primary to-primary/80 flex items-center justify-center text-[10px] font-bold text-primary-foreground shrink-0">
              {selectedPatient.firstName?.charAt(0)}{selectedPatient.lastName?.charAt(0)}
            </div>
            <span className="text-[12px] font-medium text-foreground">
              {selectedPatient.firstName} {selectedPatient.lastName}
            </span>
          </div>

          {/* Divider */}
          <div className="h-4 w-px bg-border/40" />

          {/* Sex */}
          {selectedPatient.gender && (
            <span className="flex items-center gap-1 px-1.5 py-0.5 rounded-full bg-muted/40 border border-border/20">
              <span className="text-[10px]">
                {selectedPatient.gender === 'M' || selectedPatient.gender === 'male'
                  ? '♂'
                  : selectedPatient.gender === 'F' || selectedPatient.gender === 'female'
                  ? '♀'
                  : '⚥'}
              </span>
              <span className="text-[10px] text-muted-foreground">
                {selectedPatient.gender === 'M' || selectedPatient.gender === 'male' ? 'Homme' : 
                 selectedPatient.gender === 'F' || selectedPatient.gender === 'female' ? 'Femme' : selectedPatient.gender}
              </span>
            </span>
          )}

          {/* Age */}
          {patientAge !== null && (
            <span className="text-[10px] text-muted-foreground px-1.5 py-0.5 rounded-full bg-muted/40 border border-border/20">
              {patientAge} ans
            </span>
          )}

          {/* Insurance badge */}
          {insuranceKey && insuranceKey !== 'none' && insuranceKey !== '' && (
            <span className={`px-2 py-0.5 rounded-full text-[10px] font-semibold border ${insuranceStyle.bg} ${insuranceStyle.text} ${insuranceStyle.border}`}>
              🛡️ {insuranceKey.toUpperCase()}
            </span>
          )}
          {(!insuranceKey || insuranceKey === 'none' || insuranceKey === '') && (
            <span className="px-2 py-0.5 rounded-full text-[10px] font-medium border border-border/40 bg-muted/50 text-muted-foreground">
              Sans assurance
            </span>
          )}

          {/* Allergies */}
          {allergies.length > 0 && (
            <>
              <div className="h-4 w-px bg-border/40" />
              <svg className="w-3 h-3 text-red-500 dark:text-red-400 shrink-0" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2}
                  d="M12 9v2m0 4h.01M21 12a9 9 0 11-18 0 9 9 0 0118 0z" />
              </svg>
              {allergies.slice(0, 3).map((a, i) => (
                <span key={i} className="px-1.5 py-0.5 rounded-full text-[10px] font-medium bg-red-100 dark:bg-red-900/20 border border-red-200 dark:border-red-800/30 text-red-600 dark:text-red-400">
                  {typeof a === 'string' ? a : String(a)}
                </span>
              ))}
              {allergies.length > 3 && (
                <span className="text-[10px] text-red-500 dark:text-red-400">+{allergies.length - 3}</span>
              )}
            </>
          )}

          {/* Treatment counts */}
          <div className="ml-auto flex items-center gap-3">
            <span className="text-[10px] text-muted-foreground">
              <span className="text-green-500 dark:text-green-400 font-semibold">{treatmentCounts.completed}</span> terminés
            </span>
            <span className="text-[10px] text-muted-foreground">
              <span className="text-blue-500 dark:text-blue-400 font-semibold">{treatmentCounts.planned}</span> planifiés
            </span>
            {chartLoading && (
              <svg className="h-3 w-3 animate-spin text-primary" viewBox="0 0 24 24">
                <circle className="opacity-25" cx="12" cy="12" r="10" stroke="currentColor" strokeWidth="4" fill="none" />
                <path className="opacity-75" fill="currentColor" d="M4 12a8 8 0 018-8V0C5.373 0 0 5.373 0 12h4z" />
              </svg>
            )}
          </div>
        </div>
      )}

      {/* Error banner */}
      {displayError && (
        <div className="shrink-0 mx-4 mt-1 px-3 py-1.5 bg-red-100 dark:bg-red-900/20 border border-red-300 dark:border-red-800/50 rounded-lg flex items-center gap-2">
          <svg className="h-3.5 w-3.5 text-red-500 dark:text-red-400 shrink-0" fill="none" stroke="currentColor" viewBox="0 0 24 24">
            <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M12 8v4m0 4h.01M21 12a9 9 0 11-18 0 9 9 0 0118 0z" />
          </svg>
          <span className="text-[11px] text-red-700 dark:text-red-300 flex-1">{displayError}</span>
          <button onClick={() => { clearError(); clearSeanceError(); }} className="text-red-500 dark:text-red-400 hover:text-red-700 dark:hover:text-red-300">
            <svg className="h-3 w-3" fill="none" stroke="currentColor" viewBox="0 0 24 24">
              <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M6 18L18 6M6 6l12 12" />
            </svg>
          </button>
        </div>
      )}

      {/* ── Main Content Area ── */}
      {!activeTabId ? (
        <div className="flex-1 flex items-center justify-center relative overflow-hidden">
          {/* Ambient background glow */}
          <div className="absolute inset-0 pointer-events-none">
            <div className="absolute top-1/3 left-1/2 -translate-x-1/2 -translate-y-1/2 w-96 h-96 bg-primary/[0.03] rounded-full blur-3xl" />
            <div className="absolute bottom-1/4 right-1/3 w-64 h-64 bg-primary/[0.02] rounded-full blur-3xl" />
          </div>
          <div className="text-center max-w-sm relative z-10">
            {/* Tooth icon */}
            <div className="mx-auto w-20 h-20 rounded-2xl bg-gradient-to-br from-primary/20 to-primary/10 border border-primary/15 flex items-center justify-center mb-5 shadow-lg shadow-primary/10">
              <svg className="w-10 h-10 text-primary/80" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth={1.2}>
                <path d="M12 2C9.5 2 7 3.5 7 6c0 1.5.5 3 0 6s-1 5 0 7c.5 1 1.5 2 2.5 2s1.5-1 2.5-2.5S13 16 13 16s.5 1.5 1.5 2.5S16 20.5 17 20.5s2-1 2.5-2c1-2 .5-4 0-7s0-4.5 0-6C19.5 3.5 14.5 2 12 2z" strokeLinecap="round" strokeLinejoin="round" />
              </svg>
            </div>
            <h2 className="text-xl font-semibold text-foreground mb-2 tracking-tight">
              Dossier Dentaire
            </h2>
            <p className="text-[13px] text-muted-foreground leading-relaxed">
              Recherchez un patient pour accéder au schéma dentaire, historique et actes NGAP.
            </p>
            <div className="mt-6 flex items-center justify-center gap-2 text-[11px] text-muted-foreground/60">
              <kbd className="px-2 py-0.5 rounded bg-muted/80 border border-border/40 text-muted-foreground font-mono text-[10px]">Ctrl+K</kbd>
              <span>ou utilisez la barre de recherche</span>
            </div>
          </div>
        </div>
      ) : (
        <div className="flex-1 flex overflow-hidden">

          {/* ═══ LEFT PANEL — États / NGAP ═══ */}
          {showLeftPanel && (
            <div className={`${leftPanelWidth} shrink-0 flex flex-col bg-card/95 border-r border-border/30 shadow-[4px_0_20px_rgba(0,0,0,0.08)] dark:shadow-[4px_0_20px_rgba(0,0,0,0.15)] transition-[width] duration-300`}>
              {/* Tab switcher */}
              <div className="shrink-0 flex border-b border-border/40">
                <button
                  onClick={() => setLeftPanelMode('states')}
                  className={`flex-1 py-2 text-[10px] font-semibold uppercase tracking-wider transition-colors ${
                    leftPanelMode === 'states'
                      ? 'text-amber-500 dark:text-amber-400 border-b-2 border-amber-500 dark:border-amber-400 bg-amber-500/10'
                      : 'text-muted-foreground hover:text-foreground hover:bg-muted/40'
                  }`}
                >
                  États
                </button>
                <button
                  onClick={() => setLeftPanelMode('ngap')}
                  className={`flex-1 py-2 text-[10px] font-semibold uppercase tracking-wider transition-colors ${
                    leftPanelMode === 'ngap'
                      ? 'text-teal-500 dark:text-teal-400 border-b-2 border-teal-500 dark:border-teal-400 bg-teal-500/10'
                      : 'text-muted-foreground hover:text-foreground hover:bg-muted/40'
                  }`}
                >
                  NGAP
                </button>
                <button
                  onClick={() => setLeftPanelMode('documents')}
                  className={`flex-1 py-2 text-[10px] font-semibold uppercase tracking-wider transition-colors ${
                    leftPanelMode === 'documents'
                      ? 'text-pink-500 dark:text-pink-400 border-b-2 border-pink-500 dark:border-pink-400 bg-pink-500/10'
                      : 'text-muted-foreground hover:text-foreground hover:bg-muted/40'
                  }`}
                >
                  Docs
                </button>
              </div>
              {/* Panel content */}
              <div className="flex-1 overflow-hidden">
                {leftPanelMode === 'states' ? (
                  <DentalStatesPanel
                    selectionContext={selectionContext}
                    teethData={teethData}
                    onApplyState={handleApplyState}
                    onRemoveLayer={handleRemoveLayer}
                    applyLoading={applyActLoading}
                    validationMessage={validationMessage}
                    validationGuidance={validationGuidance}
                    validationWarnings={validationWarnings}
                    onClearValidation={clearValidation}
                    onRequestConfirm={({ title, message, details, onConfirm }) => {
                      setConfirmState({
                        open: true,
                        title,
                        message,
                        details,
                        variant: 'danger',
                        confirmLabel: 'Appliquer',
                        onConfirm: () => { closeConfirm(); onConfirm(); },
                      });
                    }}
                  />
                ) : leftPanelMode === 'ngap' ? (
                  <SmartNGAPPanel
                    categories={categories}
                    selectionContext={selectionContext}
                    onActSelect={handleActSelect}
                    selectedActId={selectedAct?.id}
                    sessionExtractions={sessionExtractions}
                  />
                ) : activeTabId ? (
                  <PatientDocumentsPanel patientId={activeTabId} />
                ) : (
                  <div className="flex items-center justify-center h-full text-[11px] text-muted-foreground">
                    Sélectionnez un patient
                  </div>
                )}
              </div>
            </div>
          )}

          {/* ═══ CENTER — Toolbar + Chart + Seance bar ═══ */}
          <div className="flex-1 flex flex-col min-w-[320px] overflow-hidden">

            {/* Compact toolbar */}
            <div className="shrink-0 flex items-center gap-1.5 px-3 py-1 bg-card/80 backdrop-blur-sm border-b border-border/40">
              {/* Left panel toggle */}
              <button
                onClick={() => setShowLeftPanel(!showLeftPanel)}
                className={`text-[10px] px-2 py-1 rounded-md font-semibold transition-all flex items-center gap-1 ${
                  showLeftPanel
                    ? leftPanelMode === 'states'
                      ? 'bg-amber-500/20 text-amber-500 dark:text-amber-400 ring-1 ring-amber-500/30'
                      : leftPanelMode === 'documents'
                      ? 'bg-pink-500/20 text-pink-500 dark:text-pink-400 ring-1 ring-pink-500/30'
                      : 'bg-teal-500/20 text-teal-500 dark:text-teal-400 ring-1 ring-teal-500/30'
                    : 'text-muted-foreground hover:text-foreground hover:bg-muted/60'
                }`}
                title={showLeftPanel ? 'Masquer le panneau' : 'Afficher le panneau'}
              >
                <svg className="w-3 h-3" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                  <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M4 6h16M4 12h16M4 18h7" />
                </svg>
                {leftPanelMode === 'states' ? 'États' : leftPanelMode === 'documents' ? 'Docs' : 'NGAP'}
              </button>

              <div className="h-4 w-px bg-border/40 mx-0.5" />

              {/* Jaw view toggle */}
              <div role="radiogroup" aria-label="Vue mâchoire" className="flex items-center bg-muted rounded-lg p-0.5 border border-border/50">
                <button
                  onClick={() => setJawView('upper')}
                  role="radio"
                  aria-checked={jawView === 'upper'}
                  className={`text-[10px] px-2 py-0.5 rounded-md font-semibold transition-all ${
                    jawView === 'upper'
                      ? 'bg-primary text-primary-foreground shadow-sm'
                      : 'text-muted-foreground hover:text-foreground hover:bg-muted/80'
                  }`}
                  title="Mâchoire supérieure"
                >
                  Sup
                </button>
                <button
                  onClick={() => setJawView('full')}
                  role="radio"
                  aria-checked={jawView === 'full'}
                  className={`text-[10px] px-2 py-0.5 rounded-md font-semibold transition-all ${
                    jawView === 'full'
                      ? 'bg-primary text-primary-foreground shadow-sm'
                      : 'text-muted-foreground hover:text-foreground hover:bg-muted/80'
                  }`}
                  title="Vue complète"
                >
                  Complet
                </button>
                <button
                  onClick={() => setJawView('lower')}
                  role="radio"
                  aria-checked={jawView === 'lower'}
                  className={`text-[10px] px-2 py-0.5 rounded-md font-semibold transition-all ${
                    jawView === 'lower'
                      ? 'bg-primary text-primary-foreground shadow-sm'
                      : 'text-muted-foreground hover:text-foreground hover:bg-muted/80'
                  }`}
                  title="Mâchoire inférieure"
                >
                  Inf
                </button>
              </div>

              <div className="flex-1" />

              {/* Seance status badge */}
              {isSeanceOpen && (
                <span className="flex items-center gap-1 px-2 py-0.5 rounded-full bg-primary/10 border border-primary/20 mr-1 shrink-0">
                  <span className="relative flex h-1.5 w-1.5 shrink-0">
                    <span className="animate-ping absolute inline-flex h-full w-full rounded-full bg-primary/75 opacity-75" />
                    <span className="relative inline-flex rounded-full h-1.5 w-1.5 bg-primary" />
                  </span>
                  <span className="text-[10px] text-primary font-semibold tabular-nums">{seanceTreatments.length}</span>
                  <span className="text-[10px] text-primary/70">acte{seanceTreatments.length !== 1 ? 's' : ''}</span>
                </span>
              )}

              {/* History panel toggle */}
              <button
                onClick={() => setShowHistoryPanel(!showHistoryPanel)}
                className={`text-[10px] px-2 py-1 rounded-md font-semibold transition-all flex items-center gap-1 ${
                  showHistoryPanel
                    ? 'bg-indigo-100 dark:bg-indigo-900/40 text-indigo-600 dark:text-indigo-300 ring-1 ring-indigo-300/60 dark:ring-indigo-400/20'
                    : 'text-muted-foreground hover:text-foreground hover:bg-muted/60'
                }`}
                title={showHistoryPanel ? 'Masquer historique' : 'Afficher historique'}
              >
                Historique
                <svg className="w-3 h-3" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                  <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M12 8v4l3 3m6-3a9 9 0 11-18 0 9 9 0 0118 0z" />
                </svg>
              </button>
            </div>

            {/* Chart fills remaining space — scroll to switch jaw view */}
            <div
              className="flex-1 relative min-h-0 overflow-hidden"
              onWheel={handleJawScroll}
            >
              <DentalChartPro
                teethData={teethData}
                onSelectionChange={handleSelectionChange}
                jawView={jawView}
                patientId={activeTabId}
                className="absolute inset-0"
              />
            </div>

            {/* Bottom seance bar */}
            {isSeanceOpen && seanceTreatments.length > 0 && (
              <div className="shrink-0 flex items-center gap-3 px-4 py-1.5 bg-card/90 backdrop-blur-sm border-t border-border/40">
                <span className="text-[10px] font-semibold text-primary uppercase tracking-wide shrink-0">Séance</span>
                <div className="h-3 w-px bg-border/40 shrink-0" />
                <span className="text-[10px] text-muted-foreground shrink-0">
                  <span className="font-mono font-semibold text-foreground/80">{seanceTreatments.length}</span> acte{seanceTreatments.length !== 1 ? 's' : ''}
                </span>
                <div className="h-3 w-px bg-border/40 shrink-0" />
                <span className="text-[10px] text-muted-foreground shrink-0">
                  <span className="font-mono font-semibold text-foreground">{seanceAmounts.totalDue.toFixed(0)} DHS</span>
                </span>
                <div className="flex-1" />
                <button
                  onClick={handleCloseSeance}
                  className="shrink-0 px-3 py-1 text-[10px] font-semibold rounded-md border border-amber-300 dark:border-amber-700/40
                    bg-amber-100 dark:bg-amber-900/20 hover:bg-amber-200 dark:hover:bg-amber-900/40 text-amber-700 dark:text-amber-400 transition-colors
                    flex items-center gap-1.5 whitespace-nowrap"
                >
                  <svg className="w-3 h-3 shrink-0" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                    <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M5 13l4 4L19 7" />
                  </svg>
                  Terminer la séance
                </button>
              </div>
            )}
          </div>

          {/* ═══ RIGHT PANEL — Seance History (side panel, uses chart dead space) ═══ */}
          {showHistoryPanel && (
            <div className={`${rightPanelWidth} shrink-0 flex flex-col bg-card/95 border-l border-border/30 shadow-[-4px_0_20px_rgba(0,0,0,0.08)] dark:shadow-[-4px_0_20px_rgba(0,0,0,0.15)] transition-[width] duration-300`}>
              <SeanceHistoryTable
                seances={allSeances}
                grandTotalDue={grandTotals.totalDue}
                grandTotalPaid={grandTotals.totalPaid}
                grandTotalRemaining={grandTotals.remaining}
                onToothClick={selectTooth}
                onDeleteTreatment={handleDeleteTreatment}
                className="flex-1"
              />
            </div>
          )}

        </div>
      )}

      {/* ── Confirm Dialog (replaces native window.confirm) ── */}
      <ConfirmDialog
        open={confirmState.open}
        title={confirmState.title}
        message={confirmState.message}
        details={confirmState.details}
        variant={confirmState.variant}
        confirmLabel={confirmState.confirmLabel}
        onConfirm={confirmState.onConfirm}
        onCancel={closeConfirm}
      />
    </div>
  );
}
